/**
 * Insert panel.
 *
 * Provides actions to insert structured content into the active Word document:
 * - Matter header (reference, title, parties, phase)
 * - Reserve recommendation table (4 categories, 18 rows)
 * - Individual matter and reserve fields via a searchable browser
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  Text,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Divider,
  Input,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons/svg/add';
import { CheckmarkCircle20Regular } from '@fluentui/react-icons/svg/checkmark-circle';
import { DocumentHeader20Regular } from '@fluentui/react-icons/svg/document-header';
import { Search20Regular } from '@fluentui/react-icons/svg/search';
import { Table20Regular } from '@fluentui/react-icons/svg/table';
import { useGateway } from '../../api/useGateway';
import type { MatterSummary, ReserveData, ReportingSchema } from '../../api/gateway-client';

interface InsertPanelProps {
  matterId: string | null;
}

const BRAND_COLOR = '#075156';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },

  // ── Matter context strip ─────────────────────────────────────────────
  matterStrip: {
    padding: '8px 14px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  matterStripTitle: {
    fontSize: '12px',
    fontWeight: 600,
    flex: 1,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  matterStripRef: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
    flexShrink: 0,
  },

  // ── Quick insert section ─────────────────────────────────────────────
  quickInsert: {
    padding: '14px 14px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  insertCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  insertCardDisabled: {
    opacity: 0.5,
    cursor: 'default',
  },
  insertCardIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '6px',
    backgroundColor: BRAND_COLOR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    flexShrink: 0,
    fontSize: '20px',
  },
  insertCardIconDisabled: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground4,
  },
  insertCardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  insertCardName: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  insertCardDesc: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  insertCardAction: {
    flexShrink: 0,
  },

  // ── Toast notifications ───────────────────────────────────────────────
  toastWrap: {
    padding: '8px 14px',
  },

  // ── Field browser ─────────────────────────────────────────────────────
  fieldBrowser: {
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  fieldBrowserHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  fieldBrowserTitle: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: tokens.colorNeutralForeground3,
    flex: 1,
  },
  fieldList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  fieldItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 10px',
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    gap: '8px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  fieldItemInserted: {
    border: `1px solid ${tokens.colorPaletteGreenBorder2}`,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  fieldItemBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    flex: 1,
    minWidth: 0,
  },
  fieldName: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  fieldValue: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // ── Empty / no-matter state ──────────────────────────────────────────
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    gap: '10px',
    color: tokens.colorNeutralForeground3,
  },
  emptyIcon: {
    fontSize: '40px',
    opacity: 0.35,
    color: BRAND_COLOR,
  },
  emptyTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  emptyHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    maxWidth: '200px',
    lineHeight: '1.5',
  },
});

function formatCurrency(value: number): string {
  return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 });
}

function getClientLabel(schema: ReportingSchema | undefined, path: string, defaultLabel: string): string {
  if (!schema) return defaultLabel;
  for (const reportType of Object.values(schema.reportTypes)) {
    const field = reportType.fields.find((f) => f.path === path);
    if (field) return field.label;
  }
  return defaultLabel;
}

async function insertText(text: string): Promise<void> {
  await Word.run(async (context) => {
    context.document.getSelection().insertText(text, Word.InsertLocation.replace);
    await context.sync();
  });
}

async function insertMatterHeader(matter: MatterSummary): Promise<void> {
  const schema = matter.reportingSchema;
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const lines = [
      `LETTER OF ADVICE — RESERVE RECOMMENDATION`,
      ``,
      `${getClientLabel(schema, 'reference', 'Matter Reference')}: ${matter.reference}`,
      `${getClientLabel(schema, 'title', 'Matter Title')}: ${matter.title}`,
      `${getClientLabel(schema, 'status', 'Status')}: ${matter.status}`,
    ];
    if (matter.phase) lines.push(`${getClientLabel(schema, 'phase', 'Phase')}: ${matter.phase}`);
    if (matter.claimant?.name) lines.push(`${getClientLabel(schema, 'claimant', 'Claimant')}: ${matter.claimant.name}`);
    if (matter.defendant) lines.push(`${getClientLabel(schema, 'defendant', 'Defendant')}: ${matter.defendant}`);
    if (matter.client?.name) lines.push(`${getClientLabel(schema, 'client', 'Instructing Client')}: ${matter.client.name}`);
    if (matter.partner?.name) lines.push(`${getClientLabel(schema, 'partner', 'Responsible Partner')}: ${matter.partner.name}`);
    if (matter.claimReference) lines.push(`${getClientLabel(schema, 'claim-reference', 'Insurer Reference')}: ${matter.claimReference}`);
    lines.push(``, `Date: ${new Date().toLocaleDateString('en-AU')}`, ``, `---`, ``);

    selection.insertText(lines.join('\n'), Word.InsertLocation.replace);
    const firstParagraph = selection.paragraphs.getFirst();
    firstParagraph.load('font');
    await context.sync();
    firstParagraph.font.bold = true;
    firstParagraph.font.size = 14;
    firstParagraph.font.color = BRAND_COLOR;
    await context.sync();
  });
}

async function insertReserveTable(reserves: ReserveData, schema?: ReportingSchema): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const heading = selection.insertParagraph('RESERVE RECOMMENDATION SUMMARY', Word.InsertLocation.after);
    heading.font.bold = true;
    heading.font.size = 12;
    heading.font.color = BRAND_COLOR;
    heading.spaceAfter = 6;
    const datePara = heading.insertParagraph(
      `As at: ${new Date(reserves.asAtDate).toLocaleDateString('en-AU')}`,
      Word.InsertLocation.after,
    );
    datePara.font.size = 10;
    datePara.font.italic = true;
    datePara.spaceAfter = 12;

    const dataRows: string[][] = [['Category / Account', 'Current ($)', 'Previous ($)', 'Delta ($)']];
    for (const category of reserves.categories) {
      dataRows.push([category.name, '', '', '']);
      for (const row of category.rows) {
        const previous = row.previousAmount ?? 0;
        const delta = row.amount - previous;
        const deltaStr = delta !== 0 ? (delta > 0 ? '+' : '') + formatCurrency(delta) : '-';
        dataRows.push([`  ${getClientLabel(schema, row.accountCode, row.label)}`, formatCurrency(row.amount), formatCurrency(previous), deltaStr]);
      }
      const catPrevious = category.rows.reduce((s, r) => s + (r.previousAmount ?? 0), 0);
      const catDelta = category.subtotal - catPrevious;
      dataRows.push(['Subtotal', formatCurrency(category.subtotal), formatCurrency(catPrevious), catDelta !== 0 ? (catDelta > 0 ? '+' : '') + formatCurrency(catDelta) : '-']);
    }
    const grandPrevious = reserves.previousGrandTotal ?? 0;
    const grandDelta = reserves.grandTotal - grandPrevious;
    dataRows.push(['GRAND TOTAL', formatCurrency(reserves.grandTotal), formatCurrency(grandPrevious), grandDelta !== 0 ? (grandDelta > 0 ? '+' : '') + formatCurrency(grandDelta) : '-']);

    const table = datePara.insertTable(dataRows.length, 4, Word.InsertLocation.after, dataRows);
    table.styleBuiltIn = Word.BuiltInStyleName.gridTable4_Accent1;
    await context.sync();
  });
}

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

export const InsertPanel: React.FC<InsertPanelProps> = ({ matterId }) => {
  const styles = useStyles();
  const gateway = useGateway();
  const [isInserting, setIsInserting] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [matter, setMatter] = useState<MatterSummary | null>(null);
  const [reserves, setReserves] = useState<ReserveData | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [recentlyInserted, setRecentlyInserted] = useState<Set<string>>(new Set());
  const toastIdRef = React.useRef(0);

  const addToast = (type: 'success' | 'error', message: string) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  };

  useEffect(() => {
    if (matterId && gateway) {
      setIsLoadingData(true);
      Promise.all([
        gateway.getMatter(matterId),
        gateway.getReserves(matterId).catch(() => null),
      ]).then(([m, r]) => {
        setMatter(m);
        setReserves(r);
      }).finally(() => setIsLoadingData(false));
    } else {
      setMatter(null);
      setReserves(null);
    }
  }, [matterId, gateway]);

  const handleInsertHeader = async () => {
    if (!matter) return;
    setIsInserting('header');
    try {
      await insertMatterHeader(matter);
      addToast('success', 'Matter header inserted.');
    } catch (err: unknown) {
      addToast('error', err instanceof Error ? err.message : 'Failed to insert matter header');
    } finally {
      setIsInserting(null);
    }
  };

  const handleInsertReserves = async () => {
    if (!gateway || !matterId) return;
    setIsInserting('reserves');
    try {
      const resData = await gateway.getReserves(matterId);
      const mData = await gateway.getMatter(matterId);
      await insertReserveTable(resData, mData.reportingSchema);
      addToast('success', 'Reserve table inserted.');
    } catch (err: unknown) {
      addToast('error', err instanceof Error ? err.message : 'Failed to insert reserve table');
    } finally {
      setIsInserting(null);
    }
  };

  const handleInsertField = async (path: string, text: string) => {
    try {
      await insertText(text);
      setRecentlyInserted((prev) => new Set([...prev, path]));
      setTimeout(() => setRecentlyInserted((prev) => { const next = new Set(prev); next.delete(path); return next; }), 1500);
    } catch {
      addToast('error', 'Failed to insert field');
    }
  };

  const availableFields = useMemo(() => {
    if (!matter) return [];
    const schema = matter.reportingSchema;
    const fields: Array<{ category: string; name: string; value: string; path: string }> = [
      { category: 'Matter Details', path: 'reference', name: getClientLabel(schema, 'reference', 'Reference'), value: matter.reference },
      { category: 'Matter Details', path: 'title', name: getClientLabel(schema, 'title', 'Title'), value: matter.title },
      { category: 'Matter Details', path: 'status', name: getClientLabel(schema, 'status', 'Status'), value: matter.status },
      { category: 'Matter Details', path: 'phase', name: getClientLabel(schema, 'phase', 'Phase'), value: matter.phase ?? '-' },
      { category: 'Matter Details', path: 'claim-reference', name: getClientLabel(schema, 'claim-reference', 'Claim Reference'), value: matter.claimReference ?? '-' },
      { category: 'Matter Details', path: 'incident-date', name: getClientLabel(schema, 'incident-date', 'Incident Date'), value: matter.incidentDate ? new Date(matter.incidentDate).toLocaleDateString('en-AU') : '-' },
      { category: 'Parties', path: 'claimant', name: getClientLabel(schema, 'claimant', 'Claimant'), value: matter.claimant?.name ?? '-' },
      { category: 'Parties', path: 'defendant', name: getClientLabel(schema, 'defendant', 'Defendant'), value: matter.defendant ?? '-' },
      { category: 'Parties', path: 'client', name: getClientLabel(schema, 'client', 'Instructing Client'), value: matter.client?.name ?? '-' },
      { category: 'Parties', path: 'partner', name: getClientLabel(schema, 'partner', 'Responsible Partner'), value: matter.partner?.name ?? '-' },
    ];
    if (reserves) {
      for (const cat of reserves.categories) {
        for (const row of cat.rows) {
          fields.push({
            category: 'Current Reserves',
            path: row.accountCode,
            name: getClientLabel(schema, row.accountCode, row.label),
            value: formatCurrency(row.amount),
          });
        }
      }
    }
    return fields;
  }, [matter, reserves]);

  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return availableFields;
    const q = searchQuery.toLowerCase();
    return availableFields.filter((f) =>
      f.name.toLowerCase().includes(q) ||
      f.category.toLowerCase().includes(q) ||
      f.value.toLowerCase().includes(q)
    );
  }, [availableFields, searchQuery]);

  const fieldsByCategory = useMemo(() => {
    const groups: Record<string, typeof filteredFields> = {};
    for (const f of filteredFields) {
      if (!groups[f.category]) groups[f.category] = [];
      groups[f.category].push(f);
    }
    return groups;
  }, [filteredFields]);

  if (!gateway) {
    return (
      <div className={styles.emptyState}>
        <DocumentHeader20Regular className={styles.emptyIcon} />
        <span className={styles.emptyTitle}>Not connected</span>
        <span className={styles.emptyHint}>Sign in to insert content into your document.</span>
      </div>
    );
  }

  if (!matterId) {
    return (
      <div className={styles.emptyState}>
        <Table20Regular className={styles.emptyIcon} />
        <span className={styles.emptyTitle}>No matter selected</span>
        <span className={styles.emptyHint}>
          Open the Matter tab and select a matter to unlock insert actions.
        </span>
      </div>
    );
  }

  const disabled = isInserting !== null || isLoadingData;

  return (
    <div className={styles.container}>

      {/* ── Matter context strip ─────────────────────────────────── */}
      {matter && (
        <div className={styles.matterStrip}>
          <CheckmarkCircle20Regular style={{ color: BRAND_COLOR, flexShrink: 0 }} />
          <span className={styles.matterStripTitle}>{matter.title}</span>
          <span className={styles.matterStripRef}>{matter.reference}</span>
        </div>
      )}
      {isLoadingData && (
        <div style={{ padding: '12px 14px' }}>
          <Spinner size="tiny" label="Loading matter…" />
        </div>
      )}

      {/* ── Toast notifications ────────────────────────────────── */}
      {toasts.map((t) => (
        <div key={t.id} className={styles.toastWrap}>
          <MessageBar intent={t.type === 'success' ? 'success' : 'error'}>
            <MessageBarBody>{t.message}</MessageBarBody>
          </MessageBar>
        </div>
      ))}

      {/* ── Quick insert cards ────────────────────────────────── */}
      <div className={styles.quickInsert}>
        <span className={styles.sectionTitle}>Quick Insert</span>

        {/* Matter header card */}
        <div
          className={[styles.insertCard, disabled ? styles.insertCardDisabled : ''].filter(Boolean).join(' ')}
          onClick={disabled ? undefined : handleInsertHeader}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => { if (!disabled && e.key === 'Enter') handleInsertHeader(); }}
        >
          <div className={[styles.insertCardIcon, disabled ? styles.insertCardIconDisabled : ''].filter(Boolean).join(' ')}>
            {isInserting === 'header' ? <Spinner size="tiny" style={{ color: '#fff' }} /> : <DocumentHeader20Regular />}
          </div>
          <div className={styles.insertCardBody}>
            <span className={styles.insertCardName}>Matter Header</span>
            <span className={styles.insertCardDesc}>Reference, parties, status, and date</span>
          </div>
          <div className={styles.insertCardAction}>
            <Add20Regular style={{ color: disabled ? tokens.colorNeutralForeground4 : BRAND_COLOR }} />
          </div>
        </div>

        {/* Reserve table card */}
        <div
          className={[styles.insertCard, disabled ? styles.insertCardDisabled : ''].filter(Boolean).join(' ')}
          onClick={disabled ? undefined : handleInsertReserves}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => { if (!disabled && e.key === 'Enter') handleInsertReserves(); }}
        >
          <div className={[styles.insertCardIcon, disabled ? styles.insertCardIconDisabled : ''].filter(Boolean).join(' ')}>
            {isInserting === 'reserves' ? <Spinner size="tiny" style={{ color: '#fff' }} /> : <Table20Regular />}
          </div>
          <div className={styles.insertCardBody}>
            <span className={styles.insertCardName}>Reserve Table</span>
            <span className={styles.insertCardDesc}>
              Full 4-category table
              {reserves ? ` · ${formatCurrency(reserves.grandTotal)} total` : ''}
            </span>
          </div>
          <div className={styles.insertCardAction}>
            <Add20Regular style={{ color: disabled ? tokens.colorNeutralForeground4 : BRAND_COLOR }} />
          </div>
        </div>
      </div>

      <Divider style={{ margin: '14px 0 0' }} />

      {/* ── Field browser ─────────────────────────────────────── */}
      <div className={styles.fieldBrowser}>
        <div className={styles.fieldBrowserHeader}>
          <span className={styles.fieldBrowserTitle}>Individual Fields</span>
          {availableFields.length > 0 && (
            <Badge appearance="filled" color="informative" size="small">
              {availableFields.length}
            </Badge>
          )}
        </div>

        <Input
          placeholder="Search fields…"
          contentBefore={<Search20Regular />}
          value={searchQuery}
          onChange={(_e, data) => setSearchQuery(data.value)}
          size="small"
        />

        {isLoadingData ? (
          <Spinner size="small" label="Loading fields…" />
        ) : (
          <Accordion multiple collapsible defaultOpenItems={Object.keys(fieldsByCategory)}>
            {Object.entries(fieldsByCategory).map(([category, items]) => (
              <AccordionItem value={category} key={category}>
                <AccordionHeader size="small">
                  {category}
                  <Badge appearance="outline" color="informative" size="small" style={{ marginLeft: 6 }}>
                    {items.length}
                  </Badge>
                </AccordionHeader>
                <AccordionPanel>
                  <div className={styles.fieldList}>
                    {items.map((item) => {
                      const inserted = recentlyInserted.has(item.path);
                      return (
                        <div
                          key={item.path}
                          className={[styles.fieldItem, inserted ? styles.fieldItemInserted : ''].filter(Boolean).join(' ')}
                          onClick={() => handleInsertField(item.path, item.value)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleInsertField(item.path, item.value); }}
                          title={`Insert: ${item.value}`}
                        >
                          <div className={styles.fieldItemBody}>
                            <span className={styles.fieldName}>{item.name}</span>
                            <span className={styles.fieldValue}>{item.value}</span>
                          </div>
                          {inserted
                            ? <CheckmarkCircle20Regular style={{ color: tokens.colorPaletteGreenForeground3, flexShrink: 0 }} />
                            : <Add20Regular style={{ color: BRAND_COLOR, flexShrink: 0 }} />
                          }
                        </div>
                      );
                    })}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
    </div>
  );
};
